<template>
  <DeviceTypeSelection
    v-if="!deviceType"
    :device-type="deviceType"
    @update:device-type="(newValue) => onDeviceTypeSelection(newValue)"
  />

  <NoMountingSelector
    v-if="isNoMountingSelectorVisible"
    ref="noMountingSelectorRef"
    v-model:is-mounted="isMounted"
    v-model:failed-installation-attempt="failedInstallationAttempt"
    :device-type="deviceType"
    :installation-point="installationPoint"
  />

  <UnmountDevice
    v-if="isUnmountedDevice"
    v-model:unmounted-device="device"
    v-model:unmounted-device-event="deviceEvent"
  />

  <BaseSection
    v-if="
      deviceType === DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT &&
      !isMounted &&
      !isUnmountedDevice
    "
    heading="Adapter"
    :required="false"
  >
    <AccessoryList ref="accessoryListRef" v-model="installationPoint" />
  </BaseSection>
  <BasePrompt
    title="Dieser Raum ist als Raum ohne Heizkörper definiert."
    :proceed="onConfirmHasRadiator"
    :open="hasRadiatorPromptOpen"
    :close="useLayoutStore().closeFormDialog"
    :cancel="useLayoutStore().closeFormDialog"
    proceed-text="Heizkörper vorhanden"
  />
  <BasePrompt
    :title="dmgw6i18n.deviceForm.qtoolWarning.title"
    :question="dmgw6i18n.deviceForm.qtoolWarning.question"
    :proceed-text="dmgw6i18n.deviceForm.qtoolWarning.proceedText"
    cancel-text=""
    :open="qtoolWarningOpen"
    :close="onDmgwWarningProceed"
    :cancel="onDmgwWarningProceed"
    :proceed="onDmgwWarningProceed"
  />
  <BasePrompt
    :title="dmgw6i18n.deviceForm.gatewayWarning.title"
    :question="dmgw6i18n.deviceForm.gatewayWarning.question"
    :proceed-text="dmgw6i18n.deviceForm.gatewayWarning.proceedText"
    cancel-text=""
    :open="gatewayWarningOpen"
    :close="onDmgwWarningProceed"
    :cancel="onDmgwWarningProceed"
    :proceed="onDmgwWarningProceed"
  />
  <ImpededInstallationPrompt
    :room-id="props.parentId"
    :device-type="deviceType"
    :form-action="useFormStore().formAction"
    @reuse-installation-point="reuseInstallationPointWithDevice"
  />

  <template
    v-if="
      (isMounted || deviceType == deviceTypes.HeatCostAllocator) &&
      !isUnmountedDevice
    "
  >
    <RecordOldDevice
      v-if="
        isOldDeviceVisible && useFormStore().formAction !== FormAction.REPLACE
      "
      ref="oldFormComponentRef"
      v-model:old-device="oldDevice"
      v-model:old-device-event="oldDeviceEvent"
      :installation-point="installationPoint"
      :reset-old-device="() => initOldDevice(deviceType, installationPoint)"
    />

    <!-- Mass Exchange -->
    <DeviceVerificationForm
      v-if="isReplaceAction.value"
      ref="deviceVerificationFormRef"
      v-model:device="oldDevice"
      v-model:device-event="oldDeviceEvent"
      v-model:installation-point="installationPoint"
      v-model:tkd-service="TKDService"
    />

    <component
      :is="formComponent"
      v-if="deviceType && installationPoint && device && deviceEvent"
      :key="installationPoint?.id"
      ref="formComponentRef"
      v-model:device="currentDevice"
      v-model:device-event="deviceEvent"
      v-model:installation-point="installationPoint"
      v-model:new-battery-pack="newBatteryPack"
      v-bind="armRelatedProps"
      data-testid="deviceForm"
      @close-and-save-form="handleSubmit"
    />
  </template>
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  onBeforeMount,
  onMounted,
  provide,
  ref,
  Ref,
  watch,
} from 'vue';
import DeviceTypeSelection from '@/components/Forms/DeviceFormComponents/DeviceTypeSelection.vue';
import { useDeviceForm } from '@/composables/useDeviceForm';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { useOldDeviceForm } from '@/composables/useOldDeviceForm';
import RecordOldDevice from '@/components/Forms/DeviceFormComponents/RecordOldDevice.vue';
import NoMountingSelector from '@/components/Forms/DeviceFormComponents/NoMountingSelector.vue';
import {
  DeviceClasses,
  DeviceType,
  isLegacyDevice,
} from '@/models/devices/Device';
import {
  DeviceServiceReasonEnum,
  DeviceServiceTypeEnum,
  DeviceTypeEnum,
  deviceTypes,
  submeteringDeviceTypes,
} from '@/enums/device';
import { Radiator } from '@/models/radiators/Radiator';
import { FormAction, useFormStore } from '@/store/form/formStore';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import AccessoryList from '@/components/Forms/DeviceFormComponents/AccessoryList.vue';
import { useRoomStore } from '@/store/entities/roomStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import { useLayoutStore } from '@/store/layout/layoutStore';
import UnmountDevice from '@/components/Forms/DeviceFormComponents/UnmountDevice.vue';
import { useTKDService } from '@/composables/useTKDService';
import DeviceVerificationForm from '@/components/Forms/TkdServiceComponents/DeviceVerificationForm.vue';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { Notification } from '@/models';
import i18n from '@/utils/i18n/form.json';
import dmgw6i18n from '@/utils/i18n/dmgw6.json';
import ImpededInstallationPrompt from '@/components/Forms/DeviceFormComponents/ImpededInstallationPrompt.vue';
import { useOrderStore } from '@/store/order/orderStore';
import { useQToolConnection } from '@/composables/useQToolConnection';
import { useRouter } from 'vue-router';

const props = defineProps<{
  node?: InstallationPoint;
  parentId: string;
  payload?: any;
}>();
const emit = defineEmits(['update-entity', 'update-entities']);
const notificationStore = useNotificationStore();
const router = useRouter();

const onDmgwWarningProceed = () => {
  useLayoutStore().closeFormDialog();
  router.push({ name: 'DmgwBoard' });
};

const {
  device,
  installationPoint,
  deviceEvent,
  failedInstallationAttempt,
  initDevice,
  initAccessories,
  initInstallationPoint,
  reuseFailedInstallationPoint,
  initUnmountedDevice,
  createDevice: createNewDevice,
  updateDevice: updateNewDevice,
  updateAccessories,
} = useDeviceForm(emit);

const {
  device: oldDevice,
  deviceEvent: oldDeviceEvent,
  initOldDevice: initOldDevice,
  createDevice: createOldDevice,
  updateDevice: updateOldDevice,
} = useOldDeviceForm(emit);
const {
  initService,
  TKDDevice,
  newBatteryPack,
  TKDService,
  handleSubmit: handleSubmitTkdService,
} = useTKDService(installationPoint);

const currentDevice = computed(() => {
  if (isReplaceAction.value) {
    return TKDDevice.value;
  }
  return device.value;
});

onMounted(() => {
  if (isReplaceAction.value && props.node) {
    initService();
    TKDService.value.deviceService.type = DeviceServiceTypeEnum.EXCHANGE;
    TKDService.value.deviceService.reason = DeviceServiceReasonEnum.OLD_VERSION;
  }
});

const deviceType = ref(props.payload?.deviceType);
const hasOldDevice = ref(false);
const isMounted = ref(true);

const hasRadiatorPromptOpen = ref(false);
const qtoolWarningOpen = ref(false);
const gatewayWarningOpen = ref(false);

function isGatewayConnectedViaQTool(): boolean {
  const connection = useQToolConnection();
  return connection.isConnected.value;
}

const reuseInstallationPointWithDevice = (target: InstallationPoint) => {
  try {
    reuseFailedInstallationPoint(target);
    if (!deviceType.value || !installationPoint.value) {
      throw new Error('Installation point is not initialized');
    }
    initNewDevice(deviceType.value, installationPoint.value);
    useFormStore().resetValidation();
    useFormStore().changeCurrentNode(device.value);
    isMounted.value = true;
  } catch (error) {
    console.error(error);
    notificationStore.addNotification(
      new Notification(
        i18n.failedInstallationPrompt.errorText,
        i18n.failedInstallationPrompt.errorTitle,
        'error'
      )
    );
  }
};

const onDeviceTypeSelection = (newDeviceType: DeviceType) => {
  if (
    isDmgw6Order.value &&
    submeteringDeviceTypes.includes(newDeviceType) &&
    !isGatewayConnectedViaQTool()
  ) {
    gatewayWarningOpen.value = true;
    return;
  }

  deviceType.value = newDeviceType;
  const roomId = props.parentId;

  if (
    newDeviceType === deviceTypes.SmartRadiatorThermostat &&
    useRoomStore().getRoomById(roomId).hasRadiator === false
  ) {
    hasRadiatorPromptOpen.value = true;
  }

  initInstallationPoint(roomId);
  if (!installationPoint.value || !deviceType.value) {
    throw new Error('Something went wrong with init functions');
  }

  initNewDevice(deviceType.value, installationPoint.value);
  useFormStore().changeCurrentNode(device.value);
  useFormStore().resetChangeSnapshot();
};

const isEditAction = computed(() => {
  return useFormStore().formAction === FormAction.EDIT;
});

const isReplaceAction = computed(
  () => useFormStore().formAction === FormAction.REPLACE
);

onBeforeMount(() => {
  if (useFormStore().formAction === FormAction.CREATE) {
    initInstallationPoint(props.parentId);
    if (props.payload?.radiatorId) {
      installationPoint.value?.setRadiatorId(props.payload.radiatorId);
    }
    if (props.payload?.deviceType) {
      if (!installationPoint.value) {
        throw new Error('Installation point is not initialized');
      }
      initNewDevice(props.payload?.deviceType, installationPoint.value);
    }
  }

  if (isEditAction.value) {
    if (!props.node) {
      throw new Error('Node is not defined');
    }
    deviceType.value = props.node.supportedDeviceType;
    isMounted.value =
      !props.node?.failedInstallationAttempt && !isUnmountedDevice.value;
    initInstallationPoint(props.parentId, props.node);
    if (isUnmountedDevice.value) {
      initUnmountedDevice();
    }

    if (isMounted.value) {
      initExistingDevices(props.node);
    }
  }

  if (useFormStore().formAction === FormAction.UNMOUNT) {
    if (!props.node) {
      throw new Error('Node is not defined');
    }
    deviceType.value = props.node.supportedDeviceType;
    initInstallationPoint(props.parentId, props.node);
    initExistingDevices(props.node);
  }

  if (useFormStore().formAction === FormAction.MOUNT) {
    if (!props.node) {
      throw new Error('No node was provided');
    }
    deviceType.value = props.node.supportedDeviceType;
    initInstallationPoint(props.parentId, props.node);
    initNewDevice(deviceType.value, props.node);
    isMounted.value = true;
  }

  if (isReplaceAction.value) {
    if (!props.node) {
      throw new Error('Installation point is not initialized');
    }
    deviceType.value = props.node.supportedDeviceType;
    initInstallationPoint(props.parentId, props.node);
    initNewDevice(deviceType.value, props.node);
    if (props.node.activeDeviceId) {
      const activeDevice = props.node.getActiveDevice();
      hasOldDevice.value = true;
      initOldDevice(deviceType.value, props.node, activeDevice);
    }
    isMounted.value = !installationPoint.value?.failedInstallationAttempt;
  }
});

watch(
  () => isMounted.value,
  (isMounted) => {
    if (isMounted) {
      installationPoint.value?.removeFailedInstallationAttempt();
      if (isEditAction.value)
        if (installationPoint.value?.activeDeviceId)
          initExistingDevices(installationPoint.value);
        else initNewDevice(deviceType.value, installationPoint.value);
      if (isReplaceAction.value) {
        if (installationPoint.value?.activeDeviceId) {
          initOldDevice(
            deviceType.value,
            props.node,
            props.node?.getActiveDevice()
          );
        }
      }
    }
    updateAccessories(isMounted);
  }
);

// ARM Stuff needs refactoring
const armInstallationPoints = ref<InstallationPoint[]>([]);
const updateArmInstallationPoints = (updatedArray: InstallationPoint[]) => {
  armInstallationPoints.value = updatedArray;
};

const armRelatedProps = computed(() => {
  let armRelatedProps = {};
  if (
    deviceType.value === deviceTypes.HeatMeter ||
    deviceType.value === deviceTypes.WarmWaterMeter ||
    deviceType.value === deviceTypes.WaterMeter
  ) {
    armRelatedProps = {
      armInstallationPoints: armInstallationPoints.value,
      updateArmInstallationPoints: updateArmInstallationPoints,
    };
  }
  return armRelatedProps;
});

const isFailedInstallationAttempt = () =>
  !isMounted.value && failedInstallationAttempt.value?.reason;

// Init Devices
const initExistingDevices = (installationPoint: InstallationPoint) => {
  const activeDevice = installationPoint?.getActiveDevice() as DeviceClasses;
  deviceType.value = activeDevice.deviceType;
  initDevice(activeDevice.deviceType, activeDevice);
  initAccessories();
  initOldDevice(activeDevice.deviceType, installationPoint);

  const latestUnmountedDevice = installationPoint.getLatestUnmountedDevice();
  if (latestUnmountedDevice) {
    hasOldDevice.value = true;
  }
};

const initNewDevice = (
  deviceType: DeviceType,
  installationPoint: InstallationPoint
) => {
  initDevice(deviceType);
  initAccessories();
  initOldDevice(deviceType, installationPoint);
};

// Handle Submit
const handleSubmit = (radiator?: Radiator) => {
  if (isFailedInstallationAttempt()) {
    if (!installationPoint.value) {
      throw new Error('Installation point is not initialized');
    }

    if (deviceType.value === DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT) {
      useRoomStore().setRoomHasRadiator(props.parentId, true);
    }
    installationPoint.value?.addFailedInstallationAttempt(
      failedInstallationAttempt.value,
      deviceType.value
    );
    if (
      noMountingSelectorRef.value?.saveImage &&
      failedInstallationAttempt.value
    ) {
      noMountingSelectorRef.value?.saveImage(
        failedInstallationAttempt.value.id
      );
    }

    const updatedEntities: (InstallationPoint | Radiator)[] = [
      installationPoint.value,
    ];
    if (radiator) {
      useRoomStore().setRoomHasRadiator(props.parentId, true);
      updatedEntities.push(radiator);
    }

    emit('update-entities', updatedEntities);
    return;
  }

  if (isUnmountedDevice.value) {
    if (!installationPoint.value) {
      throw new Error('Installation point is not initialized');
    }
    if (!device.value) {
      throw new Error('Device is not initialized');
    }
    if (!deviceEvent.value) {
      throw new Error('DeviceEvent is not initialized');
    }
    installationPoint.value?.unmountDevice(
      device.value.id,
      deviceEvent.value.timestamp,
      deviceEvent.value?.counter,
      deviceEvent.value?.controlValue,
      deviceEvent.value?.reason
    );
    emit('update-entities', [installationPoint.value]);
    return;
  }

  if (useFormStore().formAction === FormAction.CREATE) {
    if (oldDevice.value?.serialNumber) {
      createOldDevice();
    }
    createNewDevice();
  }

  if (useFormStore().formAction === FormAction.MOUNT) {
    installationPoint.value?.removeFailedInstallationAttempt();

    if (oldDevice.value?.serialNumber) {
      createOldDevice();
    }

    installationPoint.value?.mountDevice(
      device.value.id,
      deviceEvent.value.timestamp,
      deviceEvent.value.counter
    );
    updateNewDevice();
  }

  if (isEditAction.value) {
    if (
      noMountingSelectorRef.value?.saveImage &&
      failedInstallationAttempt.value
    ) {
      noMountingSelectorRef.value?.saveImage(
        failedInstallationAttempt.value.id
      );
    }
    emit('update-entities', [installationPoint.value]);
    updateNewDevice();
  }

  if (isReplaceAction.value) {
    if (device.value.isLegacyDevice()) {
      installationPoint.value?.recaptureDevice(
        device.value,
        deviceEvent.value.counter
      );
      updateNewDevice();
    } else {
      const emitsList = handleSubmitTkdService();
      emit('update-entities', emitsList);
    }
  }

  if (radiator) {
    useRoomStore().setRoomHasRadiator(props.parentId, true);
    emit('update-entities', [installationPoint.value, radiator]);
  }

  if (armInstallationPoints.value.length > 0) {
    emit('update-entities', armInstallationPoints.value);
    armInstallationPoints.value = [];
  }

  if (formComponentRef.value?.saveRadiator) {
    useRoomStore().setRoomHasRadiator(props.parentId, true);
    formComponentRef.value?.saveRadiator();
  }

  if (formComponentRef.value?.saveImage) {
    if (installationPoint.value) {
      formComponentRef.value?.saveImage(installationPoint.value.id);
    }
  }
};

const onConfirmHasRadiator = () => {
  hasRadiatorPromptOpen.value = false;
};

// Toggle visibility
const isNoMountingSelectorVisible = computed(() => {
  if ((isEditAction.value && isMounted.value) || isUnmountedDevice.value) {
    return false;
  }
  if (deviceType.value === 'HEAT_COST_ALLOCATOR') {
    return useFormStore().editNode?.type === 'device';
  } else {
    return !!deviceType.value && !isLegacyDevice(deviceType.value);
  }
});

const isOldDeviceVisible = computed(() => {
  if (
    deviceType.value === DeviceTypeEnum.HEAT_COST_ALLOCATOR ||
    (deviceType.value === DeviceTypeEnum.LO_RA_WAN_GATEWAY &&
      useFormStore().formAction === FormAction.CREATE) ||
    (isEditAction.value && !hasOldDevice.value) ||
    (deviceType.value === DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT &&
      useFormStore().formAction === FormAction.CREATE) ||
    (props.node && props.node.isDeviceUnmounted())
  ) {
    return false;
  }
  return !!deviceType.value;
});

const isUnmountedDevice = computed(() => {
  if (useFormStore().formAction === FormAction.UNMOUNT) return true;

  if (!props.node) return;
  const isUnmounted = props.node.isDeviceUnmounted();
  return isEditAction.value && isUnmounted;
});

type FormComponentRef = {
  isFormValid: boolean;
  hasImage: boolean;
  handleSubmit: () => void;
  saveImage: (id: string) => void;
  saveRadiator: () => void;
};
type OldFormComponentRef = {
  isFormValid: boolean;
};
type DeviceVerificationFormRef = {
  isFormValid: boolean;
};
type NoMountingSelectorRef = {
  isFormValid: boolean;
  saveImage: (id: string) => void;
};
type AccessoryListRef = {
  isFormValid: boolean;
};
const formComponentRef: Ref<FormComponentRef | null> = ref(null);
const oldFormComponentRef: Ref<OldFormComponentRef | null> = ref(null);
const deviceVerificationFormRef: Ref<DeviceVerificationFormRef | null> =
  ref(null);
const noMountingSelectorRef: Ref<NoMountingSelectorRef | null> = ref(null);
const accessoryListRef: Ref<AccessoryListRef | null> = ref(null);

const isFormValid = computed(() => {
  if (isUnmountedDevice.value) {
    return true;
  }
  if (isFailedInstallationAttempt()) {
    return (
      noMountingSelectorRef.value?.isFormValid &&
      (accessoryListRef.value?.isFormValid ?? true)
    );
  } else {
    const isReplacementFormValid =
      oldFormComponentRef.value?.isFormValid !== false;
    return formComponentRef.value?.isFormValid && isReplacementFormValid;
  }
});

const isNoMountingFormValid = computed(() => {
  return noMountingSelectorRef.value?.isFormValid;
});

defineExpose({
  isFormValid,
  handleSubmit,
});

provide('isNoMountingFormValid', isNoMountingFormValid);
provide('isMounted', isMounted);
provide('failedInstallationAttempt', failedInstallationAttempt);
provide('oldDevice', oldDevice);
provide('oldDeviceEvent', oldDeviceEvent);
provide('resetOldDeviceFunction', () =>
  initOldDevice(deviceType.value, installationPoint.value)
);

const deviceComponents: any = {
  SMOKE_DETECTOR:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/BasicDeviceForm.vue'),
  HEAT_ENERGY_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/BasicDeviceForm.vue'),
  WARM_WATER_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/BasicDeviceForm.vue'),
  WATER_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/BasicDeviceForm.vue'),
  DIRECT_METER_GATEWAY:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/DirectMeterGatewayForm.vue'),
  HEAT_COST_ALLOCATOR:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/HeatCostAllocatorForm.vue'),
  SMART_RADIATOR_THERMOSTAT:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/SmartRadiatorThermostatForm.vue'),
  LO_RA_WAN_GATEWAY:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/LoRaWanGatewayForm.vue'),
  LEGACY_HEAT_ENERGY_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/LegacyMeteringDeviceForm.vue'),
  LEGACY_WARM_WATER_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/LegacyMeteringDeviceForm.vue'),
  LEGACY_WATER_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/LegacyMeteringDeviceForm.vue'),
  LEGACY_HEATING_PLANT_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/LegacyPlantMeterForm.vue'),
  LEGACY_WATER_PLANT_METER:
    import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/LegacyPlantMeterForm.vue'),
};

const isDmgw6Order = computed(() => {
  return useOrderStore().activeOrder?.hasDmgw6Article() ?? false;
});

const formComponent = computed(() => {
  const deviceTypeIndex = deviceType.value;
  if (deviceTypeIndex) {
    if (
      deviceTypeIndex === DeviceTypeEnum.DIRECT_METER_GATEWAY &&
      isDmgw6Order.value
    ) {
      return defineAsyncComponent({
        loader: () =>
          import('@/components/Forms/DeviceFormComponents/DeviceTypeForms/DirectMeterGateway6Form.vue'),
      });
    }
    return defineAsyncComponent({
      loader: () => deviceComponents[deviceTypeIndex],
    });
  }
  return null;
});
</script>
